import { Box, Container, Stack, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Footer } from './footer';
export const FooterFullHeightContainer = ({
  children,
  stickyChildren,
  centerVertically = false,
  header
}: {
  children: React.ReactNode;
  stickyChildren?: React.ReactNode;
  centerVertically?: boolean;
  /**
   * Full-width top of the page: a `<PageHeader />`, or a
   * `<GoBackHeaderAction />` on a page with no title. Both bring their own top
   * padding, so the container adds none. Without one, the children start with
   * their own `<PageHeader />` outside any `Container`.
   */
  header?: React.ReactNode;
}) => {
  // The bar is pinned to the viewport, so it takes no space in the flow and the
  // footer scrolls underneath it. Measure it instead of assuming a height: the
  // bars differ between pages and they wrap at narrow widths. From md up, a
  // theme with a docked action bar puts it in the flow at the end of the
  // content instead.
  const docked = useTheme().actionBar === 'fixed';
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const [stickyBarHeight, setStickyBarHeight] = useState(0);
  // Keyed off the boolean, not the node: stickyChildren is a fresh element on
  // every parent render, which would tear the observer down and back up each time.
  const hasStickyChildren = !!stickyChildren;
  useEffect(() => {
    const element = stickyBarRef.current;
    if (!element || !hasStickyChildren) {
      setStickyBarHeight(0);
      return;
    }
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setStickyBarHeight(entry.contentRect.height);
      }
    });
    observer.observe(element);
    setStickyBarHeight(element.offsetHeight);
    return () => observer.disconnect();
  }, [hasStickyChildren]);
  return <Stack sx={{
    minHeight: '100%'
  }}>
      <Box sx={{
      flexGrow: 1,
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
        {header}
        <Box sx={{
        flexGrow: 1,
        width: '100%',
        ...(centerVertically && {
          display: 'flex',
          flexDirection: 'column'
        })
      }}>
          {children}
        </Box>
      </Box>
      {stickyChildren && <Box ref={stickyBarRef} sx={{
      position: {
        xs: 'fixed',
        md: docked ? 'static' : 'fixed'
      },
      left: 0,
      right: 0,
      bottom: {
        xs: 0,
        md: '1.5rem'
      },
      zIndex: 10
    }}>
          {stickyChildren}
        </Box>}
      {/* mb from #563: 16px below the footer. Kept alongside the spacer below,
          which only reserves the pinned bar's own height. */}
      <Container sx={{
      mb: 2
    }}>
        <Footer />
      </Container>
      {stickyChildren &&
    // Lets the end of the footer clear the pinned bar.
    <Box sx={{
      height: {
        xs: stickyBarHeight,
        md: docked ? 0 : stickyBarHeight
      }
    }} />}
    </Stack>;
};